/**
 * Exchanges and returns.
 *
 * The rule that matters: a returned item is worth WHAT THE CUSTOMER PAID for it,
 * including any discount — not today's shelf price. Buy a shirt at 50% off, come
 * back and swap it for a full-price one, and you pay the real difference.
 */
import { type Minor, clampAtZero, sum } from './money.js';
import { type SaleLineTotals, computeSale, type SaleLineInput, NO_DISCOUNT } from './sale.js';

export interface ExchangeSettings {
  /** Days after purchase an exchange is allowed without a manager. */
  windowDays: number;
  /** Off by default: the difference becomes store credit instead of cash. */
  allowCashRefund: boolean;
  /** Days to look back when valuing an item returned with no receipt. */
  noReceiptLookbackDays: number;
  /** Final-sale items cannot come back without a manager override. */
  enforceFinalSale: boolean;
}

export const DEFAULT_EXCHANGE_SETTINGS: ExchangeSettings = {
  windowDays: 30,
  allowCashRefund: false,
  noReceiptLookbackDays: 90,
  enforceFinalSale: true,
};

export interface ReturnableLine {
  saleLineId: string;
  variantId: string;
  /** What was bought. */
  qty: number;
  /** How many of those already came back on an earlier exchange. */
  alreadyReturnedQty: number;
  /** The price actually paid for ONE piece, discounts included. */
  paidUnit: Minor;
  finalSale: boolean;
}

export function remainingQty(l: ReturnableLine): number {
  return clampAtZero(l.qty - l.alreadyReturnedQty);
}

export type ApprovalReason =
  | 'outside_window'
  | 'final_sale'
  | 'no_receipt'
  | 'cash_refund'
  | 'over_discount_limit'
  | 'void';

export interface ExchangeCheck {
  allowed: boolean;
  /** Empty means the cashier can do it alone; otherwise a manager PIN is needed. */
  needsApproval: ApprovalReason[];
  blocked: string[];
}

export function checkExchange(
  opts: {
    soldAt: Date;
    now?: Date;
    lines: { line: ReturnableLine; qty: number }[];
    hasReceipt: boolean;
    refundAsCash: boolean;
  },
  settings: ExchangeSettings,
): ExchangeCheck {
  const now = opts.now ?? new Date();
  const needsApproval: ApprovalReason[] = [];
  const blocked: string[] = [];

  const days = Math.floor((now.getTime() - opts.soldAt.getTime()) / 86_400_000);
  if (days > settings.windowDays) needsApproval.push('outside_window');
  if (!opts.hasReceipt) needsApproval.push('no_receipt');

  for (const { line, qty } of opts.lines) {
    if (qty > remainingQty(line)) {
      blocked.push(`Only ${remainingQty(line)} of this item can still be returned.`);
    }
    if (line.finalSale && settings.enforceFinalSale) needsApproval.push('final_sale');
  }

  if (opts.refundAsCash) {
    if (!settings.allowCashRefund) blocked.push('Cash refunds are switched off in settings.');
    else needsApproval.push('cash_refund');
  }

  return { allowed: blocked.length === 0, needsApproval: [...new Set(needsApproval)], blocked };
}

export interface ExchangeSettlement {
  returnedValue: Minor;
  newItemsValue: Minor;
  /** Positive: the customer pays this. Negative: the customer is owed this. */
  balance: Minor;
  customerPays: Minor;
  customerReceives: Minor;
  vatRate: number;
  returnedLines: SaleLineTotals[];
  newLines: SaleLineTotals[];
}

/**
 * Value the items coming back at what was paid, value the new items at their
 * current price, and report the difference.
 */
export function settleExchange(
  returning: { line: ReturnableLine; qty: number }[],
  newItems: SaleLineInput[],
  vatRate = 16,
): ExchangeSettlement {
  const returnInputs: SaleLineInput[] = returning.map(({ line, qty }) => ({
    id: `ret-${line.saleLineId}`,
    variantId: line.variantId,
    qty,
    // Valued at the price actually paid, not today's price.
    unitPrice: line.paidUnit,
    isReturn: true,
    discount: NO_DISCOUNT,
  }));

  const retTotals = computeSale(returnInputs, NO_DISCOUNT, vatRate);
  const newTotals = computeSale(newItems, NO_DISCOUNT, vatRate);

  const returnedValue = -retTotals.total; // positive number
  const newItemsValue = newTotals.total;
  const balance = newItemsValue - returnedValue;

  return {
    returnedValue,
    newItemsValue,
    balance,
    customerPays: balance > 0 ? balance : 0,
    customerReceives: balance < 0 ? -balance : 0,
    vatRate,
    returnedLines: retTotals.lines,
    newLines: newTotals.lines,
  };
}

/** Value an item that comes back with no receipt: its lowest recent selling price. */
export function noReceiptValue(recentPaidUnits: Minor[], fallback: Minor): Minor {
  if (recentPaidUnits.length === 0) return fallback;
  return Math.min(...recentPaidUnits);
}

export function creditRemaining(issued: Minor, used: Minor): Minor {
  return clampAtZero(issued - used);
}

export function creditIsUsable(c: { issued: Minor; used: Minor; expiresAt?: string | null }, now = new Date()): boolean {
  if (creditRemaining(c.issued, c.used) <= 0) return false;
  if (c.expiresAt && new Date(c.expiresAt) < now) return false;
  return true;
}

export function totalOutstandingCredit(credits: { issued: Minor; used: Minor }[]): Minor {
  return sum(credits.map((c) => creditRemaining(c.issued, c.used)));
}
